import {
  GTG_TARGET_COUNTRY_US,
  normalizeGtgTargetCountry,
} from "./gtgGeoConstants";

/**
 * Geographic targeting groups (GTG) — max CPC and audience shared by client + Cloud Functions.
 *
 * Future: optional `audienceSubcategoryId` or `tags[]` on each GTG in Firestore for finer
 * targeting without renaming these canonical audience values.
 */

/** Global minimum max-CPC (USD) for all GTG audiences until per-audience floors exist. */
export const MIN_GTG_CPC_USD = 1.5;

/** Sanity cap (USD) for advertiser-entered max CPC. */
export const MAX_GTG_CPC_USD = 999;

/** Global minimum max-CPM (USD) for video slides until per-audience floors exist. */
export const MIN_GTG_CPM_USD = 1.5;

/** Sanity cap (USD) for advertiser-entered max CPM. */
export const MAX_GTG_CPM_USD = 999;

/** @typedef {'patient' | 'provider' | 'facility'} GtgTargetAudience */

/** @typedef {'standard' | 'symptom_checker'} GtgType */

/** Standard CareMap-style geographic targeting group (default when omitted). */
export const GTG_TYPE_STANDARD = "standard";

/** Single-slide GTG whose creative also powers the standalone /symptomchecker attribution strip. */
export const GTG_TYPE_SYMPTOM_CHECKER = "symptom_checker";

/** Canonical internal path for Symptom Checker GTG slide href (after normalization). */
export const CANONICAL_SYMPTOM_CHECKER_PATH = "/symptomchecker";

/** @type {GtgTargetAudience[]} */
export const GTG_TARGET_AUDIENCES = ["patient", "provider", "facility"];

const HTTP_PREFIX = /^https?:\/\//i;
const MAILTO_PREFIX = /^mailto:/i;

/**
 * Same rules as Cloud Functions `localCarouselSyncShared.isAllowedCarouselHref`.
 * @param {unknown} href
 * @returns {boolean}
 */
export function isAllowedCarouselHref(href) {
  if (typeof href !== "string") return false;
  const t = href.trim();
  if (!t) return false;
  if (HTTP_PREFIX.test(t) || MAILTO_PREFIX.test(t)) return true;
  if (t.startsWith("/") && !t.startsWith("//")) return true;
  return false;
}

/**
 * @param {unknown} v
 * @returns {GtgType}
 */
export function normalizeGtgType(v) {
  if (v === GTG_TYPE_SYMPTOM_CHECKER) {
    return GTG_TYPE_SYMPTOM_CHECKER;
  }
  return GTG_TYPE_STANDARD;
}

/**
 * @param {string} href
 * @returns {string}
 */
function normalizeInternalPathOnly(href) {
  const t = typeof href === "string" ? href.trim() : "";
  if (!t) return "";
  const noQuery = t.split("?")[0].split("#")[0];
  if (!noQuery.startsWith("/")) return "";
  let p = noQuery;
  if (p.length > 1 && p.endsWith("/")) {
    p = p.slice(0, -1);
  }
  return p;
}

/**
 * True if href resolves to the standalone symptom checker path (relative or absolute URL).
 * @param {unknown} href
 * @returns {boolean}
 */
export function pathMatchesSymptomChecker(href) {
  const t = typeof href === "string" ? href.trim() : "";
  if (!t) return false;
  if (HTTP_PREFIX.test(t)) {
    try {
      const u = new URL(t);
      let p = u.pathname || "/";
      if (p.length > 1 && p.endsWith("/")) {
        p = p.slice(0, -1);
      }
      return p === CANONICAL_SYMPTOM_CHECKER_PATH;
    } catch {
      return false;
    }
  }
  return normalizeInternalPathOnly(t) === CANONICAL_SYMPTOM_CHECKER_PATH;
}

function normalizeGenderForSlide(g) {
  if (typeof g !== "string") return "all";
  const x = g.trim().toLowerCase();
  if (x === "male" || x === "m") return "male";
  if (x === "female" || x === "f") return "female";
  return "all";
}

/**
 * Slides that would be published after the same cleaning as zip sync (enabled, copy, link).
 * @param {unknown} rawSlides
 * @returns {{ id: string, message: string, buttonLabel: string, href: string, logoUrl: string, enabled: boolean, patientSegments: { gender: string } }[]}
 */
export function filterValidLocalCarouselSlidesForPublish(rawSlides) {
  const arr = Array.isArray(rawSlides) ? rawSlides : [];
  return arr
    .filter((s) => s && s.enabled !== false)
    .map((s) => ({
      id: typeof s.id === "string" && s.id.trim() ? s.id.trim() : "",
      message: typeof s.message === "string" ? s.message.trim() : "",
      buttonLabel:
        typeof s.buttonLabel === "string" && s.buttonLabel.trim()
          ? s.buttonLabel.trim()
          : "Learn more",
      href: typeof s.href === "string" ? s.href.trim() : "",
      logoUrl: typeof s.logoUrl === "string" ? s.logoUrl.trim() : "",
      enabled: true,
      patientSegments: {
        gender: normalizeGenderForSlide(
          s.patientSegments && s.patientSegments.gender != null
            ? s.patientSegments.gender
            : s.patientGender
        ),
      },
    }))
    .filter(
      (s) =>
        s.message &&
        s.buttonLabel &&
        s.href &&
        isAllowedCarouselHref(s.href)
    );
}

/**
 * Enforces Symptom Checker GTG rules: at most one such group; exactly one valid slide; href → /symptomchecker.
 * @param {unknown} groups
 * @returns {{ ok: true } | { ok: false, error: string }}
 */
export function validateSymptomCheckerGtgConstraints(groups) {
  if (!Array.isArray(groups)) {
    return { ok: true };
  }
  const sc = groups.filter(
    (g) => normalizeGtgType(g && g.gtgType) === GTG_TYPE_SYMPTOM_CHECKER
  );
  if (sc.length === 0) {
    return { ok: true };
  }
  if (sc.length > 1) {
    return {
      ok: false,
      error:
        "At most one Symptom Checker targeting group is allowed. Remove or convert the extra group.",
    };
  }
  const g = sc[0];
  const valid = filterValidLocalCarouselSlidesForPublish(g?.slides);
  if (valid.length !== 1) {
    return {
      ok: false,
      error:
        "Symptom Checker sponsorship needs exactly one enabled slide with sponsor name (message field), button label, and allowed link.",
    };
  }
  if (!pathMatchesSymptomChecker(valid[0].href)) {
    return {
      ok: false,
      error: `Symptom Checker slide must link to ${CANONICAL_SYMPTOM_CHECKER_PATH}.`,
    };
  }
  if (normalizeGtgTargetCountry(g?.targetCountry) !== GTG_TARGET_COUNTRY_US) {
    return {
      ok: false,
      error:
        "Symptom Checker targeting is limited to United States (zip codes) for now.",
    };
  }
  return { ok: true };
}

/** @type {Record<GtgTargetAudience, string>} */
export const GTG_TARGET_AUDIENCE_LABELS = {
  patient: "Patients (CareMap)",
  provider: "Clinicians",
  facility: "Organizations / facilities",
};

/**
 * @param {unknown} v
 * @returns {GtgTargetAudience}
 */
export function normalizeGtgTargetAudience(v) {
  if (v === "patient" || v === "provider" || v === "facility") {
    return v;
  }
  return "patient";
}

/**
 * @param {unknown} n
 * @returns {number}
 */
export function clampGtgMaxCpcUsd(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) {
    return MIN_GTG_CPC_USD;
  }
  return Math.min(MAX_GTG_CPC_USD, Math.max(MIN_GTG_CPC_USD, x));
}

/**
 * @param {unknown} n
 * @returns {number}
 */
export function clampGtgMaxCpmUsd(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) {
    return MIN_GTG_CPM_USD;
  }
  return Math.min(MAX_GTG_CPM_USD, Math.max(MIN_GTG_CPM_USD, x));
}

/**
 * Numeric bid used for carousel sort. Legacy / invalid / below-floor → 0 so explicit bids win.
 * @param {unknown} v
 * @returns {number}
 */
export function sortCpcFromListingGtgUsd(v) {
  const x = Number(v);
  if (!Number.isFinite(x) || x < MIN_GTG_CPC_USD) {
    return 0;
  }
  return Math.min(MAX_GTG_CPC_USD, x);
}

/**
 * Numeric bid for carousel sort (video / CPM row). Same clamp semantics as CPC helper.
 * @param {unknown} v
 * @returns {number}
 */
export function sortCpmFromListingGtgUsd(v) {
  const x = Number(v);
  if (!Number.isFinite(x) || x < MIN_GTG_CPM_USD) {
    return 0;
  }
  return Math.min(MAX_GTG_CPM_USD, x);
}

/**
 * @param {unknown} usd
 * @returns {number} integer cents, non-negative
 */
export function gtgMaxCpcUsdToChargeCents(usd) {
  const x = Number(usd);
  if (!Number.isFinite(x) || x < MIN_GTG_CPC_USD) {
    return 0;
  }
  const clamped = Math.min(MAX_GTG_CPC_USD, x);
  return Math.round(clamped * 100);
}

/**
 * One carousel impression for a CPM-priced video slide: (CPM USD / 1000) as cents, min 1 cent when in range.
 * @param {unknown} usd
 * @returns {number} integer cents
 */
export function gtgMaxCpmUsdToChargeCentsPerImpression(usd) {
  const x = Number(usd);
  if (!Number.isFinite(x) || x < MIN_GTG_CPM_USD) {
    return 0;
  }
  const clamped = Math.min(MAX_GTG_CPM_USD, x);
  const centsFloat = (clamped * 100) / 1000;
  return Math.max(1, Math.round(centsFloat));
}

/**
 * @param {{ maxCpcUsd?: unknown, targetAudience?: unknown }} group
 * @returns {{ ok: true, maxCpcUsd: number, targetAudience: GtgTargetAudience } | { ok: false, error: string }}
 */
export function validateGtgPricingFields(group) {
  const targetAudience = normalizeGtgTargetAudience(group?.targetAudience);
  const raw = group?.maxCpcUsd;
  const x = Number(raw);
  if (!Number.isFinite(x) || x < MIN_GTG_CPC_USD || x > MAX_GTG_CPC_USD) {
    return {
      ok: false,
      error: `Each targeting group needs a max CPC between $${MIN_GTG_CPC_USD} and $${MAX_GTG_CPC_USD} USD (Healthcare ad rate reference).`,
    };
  }
  return { ok: true, maxCpcUsd: x, targetAudience };
}

/**
 * @param {{ maxCpmUsd?: unknown, targetAudience?: unknown }} group
 * @returns {{ ok: true, maxCpmUsd: number, targetAudience: GtgTargetAudience } | { ok: false, error: string }}
 */
export function validateGtgCpmPricingFields(group) {
  const targetAudience = normalizeGtgTargetAudience(group?.targetAudience);
  const raw = group?.maxCpmUsd;
  const x = Number(raw);
  if (!Number.isFinite(x) || x < MIN_GTG_CPM_USD || x > MAX_GTG_CPM_USD) {
    return {
      ok: false,
      error: `Video slides need a max CPM between $${MIN_GTG_CPM_USD} and $${MAX_GTG_CPM_USD} USD (Healthcare ad rate reference).`,
    };
  }
  return { ok: true, maxCpmUsd: x, targetAudience };
}

const CPC_RANGE_HINT = `between $${MIN_GTG_CPC_USD} and $${MAX_GTG_CPC_USD} USD (Healthcare ad rate reference)`;

const CPM_RANGE_HINT = `between $${MIN_GTG_CPM_USD} and $${MAX_GTG_CPM_USD} USD (Healthcare ad rate reference)`;

/**
 * Inline / field-level max CPC check (UI). Does not accept empty / non-numeric.
 * @param {unknown} raw
 * @returns {{ ok: true } | { ok: false, error: string }}
 */
export function validateGtgMaxCpcUsdInput(raw) {
  const x = Number(raw);
  if (!Number.isFinite(x) || x < MIN_GTG_CPC_USD || x > MAX_GTG_CPC_USD) {
    return {
      ok: false,
      error: `Max CPC must be ${CPC_RANGE_HINT}.`,
    };
  }
  return { ok: true };
}

/**
 * Inline / field-level max CPM check (UI).
 * @param {unknown} raw
 * @returns {{ ok: true } | { ok: false, error: string }}
 */
export function validateGtgMaxCpmUsdInput(raw) {
  const x = Number(raw);
  if (!Number.isFinite(x) || x < MIN_GTG_CPM_USD || x > MAX_GTG_CPM_USD) {
    return {
      ok: false,
      error: `Max CPM must be ${CPM_RANGE_HINT}.`,
    };
  }
  return { ok: true };
}

/**
 * @param {unknown} href
 * @returns {string | null} 11-char video id or null
 */
export function extractYoutubeVideoId(href) {
  if (typeof href !== "string") {
    return null;
  }
  const t = href.trim();
  if (!t) {
    return null;
  }
  let u;
  try {
    u = /^https?:\/\//i.test(t) ? new URL(t) : new URL(t, "https://www.youtube.com");
  } catch {
    return null;
  }
  const fullHost = (u.hostname || "").toLowerCase();
  const host = fullHost.replace(/^www\./i, "");
  const isYt =
    host === "youtu.be" ||
    host === "m.youtube.com" ||
    host === "music.youtube.com" ||
    host === "youtube.com" ||
    fullHost.endsWith(".youtube.com");

  if (host === "youtu.be") {
    const id = u.pathname.replace(/^\//, "").split("/")[0];
    return /^[a-zA-Z0-9_-]{11}$/.test(id) ? id : null;
  }

  if (!isYt) {
    return null;
  }

  const v = u.searchParams.get("v");
  if (v && /^[a-zA-Z0-9_-]{11}$/.test(v)) {
    return v;
  }

  const parts = u.pathname.split("/").filter(Boolean);
  const embedIdx = parts.indexOf("embed");
  if (embedIdx >= 0 && parts[embedIdx + 1] && /^[a-zA-Z0-9_-]{11}$/.test(parts[embedIdx + 1])) {
    return parts[embedIdx + 1];
  }
  const shortsIdx = parts.indexOf("shorts");
  if (shortsIdx >= 0 && parts[shortsIdx + 1] && /^[a-zA-Z0-9_-]{11}$/.test(parts[shortsIdx + 1])) {
    return parts[shortsIdx + 1];
  }
  const liveIdx = parts.indexOf("live");
  if (liveIdx >= 0 && parts[liveIdx + 1] && /^[a-zA-Z0-9_-]{11}$/.test(parts[liveIdx + 1])) {
    return parts[liveIdx + 1];
  }

  return null;
}

/**
 * @param {unknown} href
 * @returns {boolean}
 */
export function isYoutubeVideoUrl(href) {
  return extractYoutubeVideoId(href) != null;
}

/**
 * In-app help: URL shapes accepted for geographic targeting “Link to video” (must match {@link extractYoutubeVideoId}).
 */
export const GTG_YOUTUBE_LINK_HELP_LINES = [
  "Easiest: open the video in a browser and copy the URL from the address bar (usually youtube.com/watch?v=…).",
  "Also accepted: youtu.be/…, youtube.com/shorts/…, /embed/…, /live/…, and m.youtube.com or music.youtube.com links that include the same 11-character video id.",
  "Save & sync normalizes accepted links to a standard youtube.com/watch?v=… form.",
  "Not supported: Vimeo, TikTok, Facebook, or direct .mp4 / file-host links — YouTube only.",
];

/**
 * @param {unknown} href
 * @returns {string}
 */
export function canonicalYoutubeWatchUrlFromHref(href) {
  const id = extractYoutubeVideoId(href);
  return id ? `https://www.youtube.com/watch?v=${id}` : "";
}
